import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { Loader2, CheckCircle2, Landmark, CreditCard, Copy, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

const PROVIDER_LABEL = { STRIPE: "Stripe (Test)", PAYPAL: "PayPal (Test)", BANK_TRANSFER: "Überweisung / Vorkasse" };

export default function CheckoutPayPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = (params.get("provider") || "").toUpperCase();

  const stored = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(`pay_${sessionId}`) || "{}"); } catch (_e) { return {}; }
  }, [sessionId]);

  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState("");

  const isBank = provider === "BANK_TRANSFER";
  const bank = stored.bank_instructions || {};
  const amount = Number(stored.total_amount || bank.amount || 0).toFixed(2);
  const items = stored.item_titles || [];
  const orderNumber = stored.order_number || (sessionId ? `PMC-${sessionId.slice(0, 8).toUpperCase()}` : "");

  useEffect(() => {
    if (!sessionId) navigate("/cart");
  }, [sessionId, navigate]);

  const confirmMock = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/cart/social/mock-confirm", { session_id: sessionId });
      setPaid(true);
      try { sessionStorage.removeItem(`pay_${sessionId}`); } catch (_e) { /* noop */ }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || t("pay.error", { defaultValue: "Zahlung konnte nicht abgeschlossen werden." })));
    } finally {
      setBusy(false);
    }
  };

  const copy = (txt) => { try { navigator.clipboard.writeText(txt || ""); } catch (_e) { /* noop */ } };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4" data-testid="checkout-pay-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-[#EEE] p-6">
        <button onClick={() => navigate("/cart")} className="flex items-center gap-1 text-sm text-[#666] hover:text-[#0A0A0A] mb-4" data-testid="pay-back-btn">
          <ArrowLeft size={16} /> {t("common.back", { defaultValue: "Zurück" })}
        </button>

        {paid ? (
          <div className="text-center py-6" data-testid="pay-success">
            <div className="w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={28} />
            </div>
            <h2 className="text-xl font-semibold mb-1">{t("pay.paid_title", { defaultValue: "Zahlung bestätigt" })}</h2>
            <p className="text-sm text-[#666] mb-6">{t("pay.paid_sub", { defaultValue: "Deine digitalen Inhalte sind jetzt freigeschaltet." })}</p>
            <button onClick={() => navigate("/profile/purchases")} data-testid="pay-goto-purchases" className="w-full py-3 rounded-xl bg-[#0A0A0A] text-white text-sm font-medium hover:opacity-90">
              {t("pay.goto_purchases", { defaultValue: "Zu meinen Käufen" })}
            </button>
          </div>
        ) : isBank ? (
          <div data-testid="pay-bank">
            <div className="flex items-center gap-2 mb-3"><Landmark size={20} /><h2 className="text-lg font-semibold">{PROVIDER_LABEL.BANK_TRANSFER}</h2></div>
            <InfoCard variant="warning" testId="pay-bank-pending" className="mb-4 rounded-xl">
              {t("pay.bank_pending", { defaultValue: "Warte auf Zahlungseingang. Deine Inhalte werden nach Eingang der Überweisung automatisch freigeschaltet." })}
            </InfoCard>
            <p className="text-sm text-[#666] mb-3">{t("pay.bank_intro", { defaultValue: "Bitte überweise den Betrag mit folgendem Verwendungszweck:" })}</p>
            <dl className="text-sm divide-y divide-[#F0F0F0] border border-[#EEE] rounded-xl overflow-hidden">
              {items.length > 0 && (
                <div className="flex justify-between px-4 py-2.5"><dt className="text-[#999]">{t("pay.for", { defaultValue: "Für" })}</dt><dd className="font-medium text-right ml-4" data-testid="pay-bank-items">{items.join(", ")}</dd></div>
              )}
              <div className="flex justify-between px-4 py-2.5"><dt className="text-[#999]">{t("pay.amount", { defaultValue: "Betrag" })}</dt><dd className="font-semibold" data-testid="pay-bank-amount">{amount} EUR</dd></div>
              <div className="flex justify-between px-4 py-2.5 items-center"><dt className="text-[#999]">IBAN</dt><dd className="font-medium flex items-center gap-2">{bank.iban || "—"}<Copy size={14} className="cursor-pointer text-[#999]" onClick={() => copy(bank.iban)} /></dd></div>
              <div className="flex justify-between px-4 py-2.5"><dt className="text-[#999]">BIC</dt><dd className="font-medium">{bank.bic || "—"}</dd></div>
              <div className="flex justify-between px-4 py-2.5"><dt className="text-[#999]">{t("pay.holder", { defaultValue: "Empfänger" })}</dt><dd className="font-medium">{bank.account_holder || "stilltouched"}</dd></div>
              <div className="flex justify-between px-4 py-2.5 items-center"><dt className="text-[#999]">{t("pay.reference", { defaultValue: "Verwendungszweck" })}</dt><dd className="font-semibold flex items-center gap-2" data-testid="pay-bank-reference">{orderNumber}<Copy size={14} className="cursor-pointer text-[#999]" onClick={() => copy(orderNumber)} /></dd></div>
            </dl>
            <p className="text-xs text-[#999] mt-3">{t("pay.bank_email_note", { defaultValue: "Wir haben Dir die Überweisungsdaten zusätzlich per E-Mail geschickt." })}</p>
            <button onClick={() => navigate("/profile/purchases")} className="mt-5 w-full py-3 rounded-xl border border-[#DDD] text-sm font-medium hover:bg-[#FAFAFA]" data-testid="pay-bank-done">
              {t("pay.understood", { defaultValue: "Verstanden" })}
            </button>
          </div>
        ) : (
          <div data-testid="pay-mock">
            <div className="flex items-center gap-2 mb-3"><CreditCard size={20} /><h2 className="text-lg font-semibold">{PROVIDER_LABEL[provider] || provider}</h2></div>
            <InfoCard variant="info" size="sm" testId="pay-mock-notice" className="mb-4 rounded-xl">
              {t("pay.mock_notice", { defaultValue: "TESTMODUS — es findet keine echte Abbuchung statt. Klicke, um eine erfolgreiche Zahlung zu simulieren." })}
            </InfoCard>
            <dl className="text-sm divide-y divide-[#F0F0F0] border border-[#EEE] rounded-xl overflow-hidden mb-4">
              {items.length > 0 && (
                <div className="flex justify-between px-4 py-2.5"><dt className="text-[#999]">{t("pay.for", { defaultValue: "Für" })}</dt><dd className="font-medium text-right ml-4">{items.join(", ")}</dd></div>
              )}
              <div className="flex justify-between px-4 py-2.5"><dt className="text-[#999]">{t("pay.amount", { defaultValue: "Betrag" })}</dt><dd className="font-semibold" data-testid="pay-mock-amount">{amount} EUR</dd></div>
              <div className="flex justify-between px-4 py-2.5"><dt className="text-[#999]">{t("pay.order", { defaultValue: "Bestellung" })}</dt><dd className="font-medium">{orderNumber}</dd></div>
            </dl>
            {error && <p className="text-sm text-red-600 mb-3" data-testid="pay-error">{error}</p>}
            <button onClick={confirmMock} disabled={busy} data-testid="pay-mock-confirm" className="w-full py-3 rounded-xl bg-[#0A0A0A] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <><Loader2 size={16} className="animate-spin" /> {t("pay.processing", { defaultValue: "Zahlung wird verarbeitet…" })}</> : t("pay.confirm", { defaultValue: "Zahlung abschließen" })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
